/** @jsx jsx */
import _ from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTimeoutFn } from 'react-use'
import AutoSizer from 'react-virtualized-auto-sizer'
import { ListChildComponentProps, VariableSizeList } from 'react-window'
import { jsx } from 'theme-ui'
import { Box, Button, Flex, FormattedCurrency, List, ListItem, Txt } from '../../components'
import { ButtonBlock } from '../../components/ButtonBlock'
import { FormattedTime } from '../../components/FormattedTime'
import { FormattedUserName } from '../../components/FormattedUserName'
import { UserProfilePicture } from '../../components/UserProfilePicture'
import {
  ClientGameHandController,
  IClientUserData,
} from '../../controllers/ClientGameHandController'
import { theme } from '../../gatsby-plugin-theme-ui/theme'
import { ID, Model } from '../../sharedBetweenServerAndClient__DO_NOT_EDIT/types'
import { GameHandDetailsModal, GameHandDetailsModalHeader } from './GameHandDetailsModal'
import { IPositioningMeasurements } from './positionProps'
import { IGameData } from './useGameData'
import { getUserHandSolution } from '../../utils/getUserHandSolution'
import { Modal } from '../../components/Modal'

const FormattedAction: React.SFC<{ action: Model.GameHandAction }> = ({ action }) => {
  switch (action.type) {
    case 'BET': {
      return (
        <Box>
          {(() => {
            switch (action.betType) {
              case 'bet':
                return 'Bet'
              case 'call':
                return 'Called'
              case 'raise':
                return 'Raised to'
            }
          })()}{' '}
          <FormattedCurrency amount={action.chipsAmount} />
        </Box>
      )
    }
    case 'CHECK': {
      return (
        <Box>
          Checked{action.wasTriggeredByAway && <React.Fragment> (user was away)</React.Fragment>}
        </Box>
      )
    }
    case 'FOLD': {
      return (
        <Box>
          Folded{action.wasTriggeredByAway && <React.Fragment> (user was away)</React.Fragment>}
        </Box>
      )
    }
  }
}

interface IEventProps {
  content: React.ReactNode
  height: number
  userData?: IClientUserData
  datetimeCreated: Date
  type: 'action' | 'hand-result' | 'new-hand'
}

const ChatBubble: React.SFC<{ arrowDirection: 'left' | 'right'; bg: string }> = ({
  arrowDirection,
  bg,
  children,
  ...props
}) => {
  return (
    <Txt
      sx={{
        mt: 2,
        py: 3,
        px: 4,
        bg: bg,
        color: 'lightText',
        borderRadius: 'pill',
        lineHeight: 1,
        position: 'relative',
        boxShadow: '0px 0px 1px 1px rgba(0,0,0,0.05)',
        ':after': {
          content: "''",
          position: 'absolute',
          [arrowDirection]: '6px',
          top: '50%',
          width: '0',
          height: '0',
          border: '12px solid transparent',
          borderTop: '0px solid transparent',
          marginTop: '-12px',
          ...(arrowDirection === 'left'
            ? {
                borderRightColor: bg,
                borderLeft: '0px solid transparent',
                marginLeft: '-12px',
              }
            : {
                borderLeftColor: bg,
                borderRight: '0px solid transparent',
                marginRight: '-12px',
              }),
        },
      }}
      {...props}
    >
      {children}
    </Txt>
  )
}

class Row extends React.PureComponent<
  Omit<ListChildComponentProps, 'data'> & {
    data: { formattedEvents: IEventProps[]; controller: ClientGameHandController }
  }
> {
  render() {
    const {
      index,
      style,
      data: { formattedEvents, controller },
    } = this.props
    const { userData, datetimeCreated, content, type } = formattedEvents[index]
    const isCurrentUser = userData && controller.currentUserData.id === userData.id
    if (type === 'new-hand') {
      return (
        <Box sx={{ px: 4 }} key={index} style={style}>
          {content}
        </Box>
      )
    }
    return (
      <Flex
        style={style}
        key={index}
        sx={{ px: 4, flexDirection: isCurrentUser ? 'row-reverse' : 'row' }}
      >
        <Box sx={{ flex: '0 0 50px', textAlign: 'center' }}>
          {userData && <UserProfilePicture width={40} user={userData} />}
          <Txt sx={{ color: 'gray.4', fontSize: 0, mt: 1 }}>
            <FormattedTime datetime={datetimeCreated} />
          </Txt>
        </Box>
        <Box ml={3}>
          {userData && (
            <Txt sx={{ fontWeight: 'bold' }}>
              <FormattedUserName user={userData} />
            </Txt>
          )}
          {content}
        </Box>
      </Flex>
    )
  }
}

const GameHandDetailsModalAndButton: React.SFC<{
  gameHand: Model.GameHand
  usersMap: Record<ID, Model.User>
  currentUserId: string
}> = ({ gameHand, usersMap, currentUserId }) => {
  const [showModal, setShowModal] = useState(false)
  return (
    <React.Fragment>
      <Button variant="link" onClick={() => setShowModal(true)}>
        View Final Result
      </Button>
      <Modal
        label={'Game Details Modal'}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        header={<GameHandDetailsModalHeader />}
      >
        {showModal ? (
          <GameHandDetailsModal {...{ gameHand, usersMap, currentUserId }} />
        ) : (
          <Box sx={{ height: 200, width: '100%' }} />
        )}
      </Modal>
    </React.Fragment>
  )
}

const getFormattedEvents = (gameData: IGameData, controller: ClientGameHandController) => {
  const usersMap = _.keyBy(gameData.users, (user) => user.id)
  const { currentUserId } = controller
  const formattedEvents: Array<IEventProps> = []
  _.forEachRight(gameData.gameHands, (gameHand, index) => {
    const previousHand = gameData.gameHands[index + 1]
    const givenChips = previousHand && previousHand.pendingActionsForNextHand?.givenChips
    formattedEvents.push({
      content: (
        <Box sx={{ fontWeight: 'light', fontSize: 1 }}>
          <Txt>New hand started:</Txt>
          <Txt>
            <Txt as="span" sx={{ fontWeight: 'normal' }}>
              Blinds:
            </Txt>{' '}
            <FormattedCurrency amount={gameHand.rules.blindAmount / 2} />/
            <FormattedCurrency amount={gameHand.rules.blindAmount} />
          </Txt>
          <Txt>
            <Txt as="span" sx={{ fontWeight: 'normal' }}>
              Dealer:
            </Txt>{' '}
            <FormattedUserName user={controller.getUserData(gameHand.dealerUserId)} />
          </Txt>
          {givenChips && (
            <Txt>
              <Txt sx={{ fontWeight: 'normal' }}>Chips given:</Txt>
              <List>
                {_.map(previousHand.pendingActionsForNextHand?.givenChips, (amount, userId) => (
                  <ListItem key={userId}>
                    <FormattedUserName user={usersMap[userId]} /> -{' '}
                    <FormattedCurrency amount={amount} />
                  </ListItem>
                ))}
              </List>
            </Txt>
          )}
        </Box>
      ),
      height: _.values(givenChips).length * 23 + (!!_.values(givenChips).length ? 18 : 0) + 80,
      datetimeCreated: new Date(gameHand.datetimeCreated),
      type: 'new-hand',
    })

    _.forEach(gameHand.bettingRounds, ({ actions }) => {
      _.forEach(actions, (action) => {
        const userData = controller.getUserData(action.userId)
        formattedEvents.push({
          content: (
            <ChatBubble
              bg={'white'}
              sx={{ color: 'lightText' }}
              arrowDirection={userData.isCurrentUser ? 'right' : 'left'}
            >
              <FormattedAction action={action} />
            </ChatBubble>
          ),
          height: 80,
          userData,
          datetimeCreated: new Date(action.datetimeCreated),
          type: 'action',
        })
      })
    })
    gameHand.finalResult?.automaticallyVisibleUserHands.forEach((visibleHand) => {
      if (visibleHand.amountWon > 0) {
        const userData = controller.getUserData(visibleHand.userId)
        const solution = getUserHandSolution({ gameHand, cards: visibleHand.cards })
        formattedEvents.push({
          datetimeCreated: new Date(gameHand.finalResult!.datetimeCompleted),
          content: (
            <React.Fragment>
              <ChatBubble
                bg={'green'}
                sx={{ color: 'white' }}
                arrowDirection={userData.isCurrentUser ? 'right' : 'left'}
              >
                won <FormattedCurrency amount={visibleHand.amountWon} /> with {solution.descr}
              </ChatBubble>
              <Box>
                <GameHandDetailsModalAndButton {...{ gameHand, usersMap, currentUserId }} />
              </Box>
            </React.Fragment>
          ),
          height: 120,
          userData,
          type: 'hand-result',
        })
      }
    })
    if (gameHand.finalResult?.handEndedPrematurelyInfo) {
      const { amountWon, userId } = gameHand.finalResult?.handEndedPrematurelyInfo
      const userData = controller.getUserData(userId)
      formattedEvents.push({
        datetimeCreated: new Date(gameHand.finalResult!.datetimeCompleted),
        content: (
          <React.Fragment>
            <ChatBubble
              bg={'green'}
              sx={{ color: 'white' }}
              arrowDirection={userData.isCurrentUser ? 'right' : 'left'}
            >
              won <FormattedCurrency amount={amountWon} /> (all other players folded)
            </ChatBubble>
            <Box>
              <GameHandDetailsModalAndButton {...{ gameHand, usersMap, currentUserId }} />
            </Box>
          </React.Fragment>
        ),
        height: 120,
        userData,
        type: 'hand-result',
      })
    }
  })
  return formattedEvents
}

export const GameChat: React.SFC<{
  measurements: IPositioningMeasurements
  gameData: IGameData
  controller: ClientGameHandController
  hasGameChatVisible: boolean
  setHasGameChatVisible: (hasGameChatVisible: boolean) => void
}> = ({
  measurements,
  gameData,
  setHasGameChatVisible,
  controller,
  hasGameChatVisible,
  ...props
}) => {
  const listRef = useRef<VariableSizeList>(null)
  const formattedEvents = useMemo(() => {
    return getFormattedEvents(gameData, controller)
  }, [gameData])
  // const [count, setCount] = useState(10)
  // const formattedEvents = _formattedEvents.slice(0, count)
  // useInterval(() => {
  //   setCount(count + 10)
  // }, 1500)
  useEffect(() => {
    if (listRef.current && hasGameChatVisible) {
      listRef.current.scrollToItem(formattedEvents.length - 1)
    }
  }, [formattedEvents[formattedEvents.length - 1], hasGameChatVisible])
  useTimeoutFn(() => {
    if (listRef.current) {
      listRef.current.scrollToItem(formattedEvents.length - 1)
    }
  }, 0)
  return (
    <Box
      sx={{
        borderRight: `1px solid ${theme.colors.border}`,
        height: '100vh',
        width: '300px',
        position: 'fixed',
        zIndex: 5,
        bg: 'background',
      }}
      {...props}
    >
      <Flex sx={{ borderBottom: `1px solid ${theme.colors.border}` }}>
        <ButtonBlock
          onClick={() => setHasGameChatVisible(false)}
          sx={{
            p: [3, null, null, null, 4],
            fontSize: [1, null, null, 2],
            textAlign: 'left',
            color: 'lightText',
          }}
        >
          &#8592; Close History
        </ButtonBlock>
        {/* <ButtonBlock sx={{ p: 4 }}></ButtonBlock> */}
      </Flex>
      <Box sx={{ height: [`calc(100vh - 33px)`, null, null, null, `calc(100vh - 51px)`] }}>
        <AutoSizer>
          {({ width, height }) => (
            <VariableSizeList
              ref={listRef}
              height={height}
              width={width}
              itemSize={(index) => {
                return formattedEvents[index].height
              }}
              itemCount={formattedEvents.length}
              itemData={{ formattedEvents, controller }}
            >
              {Row}
            </VariableSizeList>
          )}
        </AutoSizer>
      </Box>
    </Box>
  )
}
